import { useState, useEffect } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/all';
import { InteractiveGlobe } from './ui/interactive-globe';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const [globeSize, setGlobeSize] = useState(800);

  useEffect(() => {
    const handleResize = () => {
      // Desktop strictly 800. Tablet 650. Mobile 550.
      if (window.innerWidth < 768) {
        setGlobeSize(550);
      } else if (window.innerWidth < 1024) {
        setGlobeSize(650);
      } else {
        setGlobeSize(800);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useGSAP(() => {
    // Initial entry animations
    gsap.fromTo('#hero-heading',
      { opacity: 0, x: -50 },
      { opacity: 1, x: 0, delay: 0.3, duration: 1 }
    );
    gsap.fromTo('#hero-subheading',
      { opacity: 0, x: -30 },
      { opacity: 1, x: 0, delay: 0.8, duration: 1 }
    );
    gsap.to('#hero-img', { opacity: 1, scale: 1, delay: 1, duration: 1.5 });

    // Scroll-driven animations (locked to prevent disappearing)
    gsap.to('#hero-heading', {
      scrollTrigger: {
        trigger: '#hero',
        start: 'top top',
        end: '+=400',
        scrub: true,
      },
      y: -20,
      immediateRender: false,
    });

    gsap.to('#hero-subheading', {
      scrollTrigger: {
        trigger: '#hero',
        start: 'top top',
        end: '+=300',
        scrub: true,
      },
      y: -10,
      immediateRender: false,
    });

    gsap.to('#hero-bg-img', {
      scrollTrigger: {
        trigger: 'section',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
      scale: 1.1,
      y: 100,
      immediateRender: false,
    });
  }, []);

  // Determine globe colors based on theme if possible, or use standard blue
  const globeColor = "rgba(76, 195, 229, ALPHA)"; // Brand Blue
  const globeArc = "rgba(76, 195, 229, 0.4)";
  const globeMarker = "rgba(76, 195, 229, 1)";

  // Global Capitals Configuration (including Lilongwe and Blantyre)
  const globalMarkers = [
    { lat: -13.96, lng: 33.77, label: "Lilongwe" },
    { lat: -15.77, lng: 35.00, label: "Blantyre" },
    { lat: 51.51, lng: -0.13, label: "London" },
    { lat: 35.68, lng: 139.69, label: "Tokyo" },
    { lat: 40.71, lng: -74.01, label: "New York" },
    { lat: 48.85, lng: 2.35, label: "Paris" },
    { lat: -33.87, lng: 151.21, label: "Sydney" },
    { lat: 1.35, lng: 103.82, label: "Singapore" },
    { lat: -11.45, lng: 34.00, label: "Mzuzu" },
  ];

  const globalConnections = [
    { from: [-13.96, 33.77], to: [51.51, -0.13] },  // Lilongwe to London
    { from: [-15.77, 35.00], to: [1.35, 103.82] },  // Blantyre to Singapore
    { from: [51.51, -0.13], to: [40.71, -74.01] }, // London to New York
    { from: [35.68, 139.69], to: [-33.87, 151.21] }, // Tokyo to Sydney
    { from: [48.85, 2.35], to: [35.68, 139.69] },   // Paris to Tokyo
    { from: [-11.45, 34.00], to: [35.68, 139.69] }, // Mzuzu to Tokyo
  ];

  return (
    <section id="hero" className="w-full min-h-screen bg-slate-50 dark:bg-black relative flex items-center justify-center overflow-visible lg:overflow-hidden py-24 lg:py-0 transition-colors duration-500">

      {/* Background Visual */}
      <div
        id="hero-img"
        className="absolute inset-0 w-full h-full flex items-center justify-center opacity-0 scale-90 transition-all z-0"
      >
        <img
          id="hero-bg-img"
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop"
          alt="Hero Background"
          className="w-full h-full object-cover pointer-events-none opacity-5 md:opacity-10 dark:opacity-20 dark:md:opacity-40 transition-opacity grayscale"
        />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />

      {/* Main Content Container - REMOVED z-[100] to allow inner layering */}
      <div className="w-full flex flex-col lg:flex-row items-center justify-center lg:justify-between px-6 sm:px-20 lg:px-32 gap-12 lg:gap-10">

        {/* Left Side: Text Content - Higher z-index to stay above robot */}
        <div className="flex-1 flex flex-col items-center lg:items-start justify-center text-center lg:text-left relative z-50 pt-10 lg:pt-0 mt-4 lg:mt-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 px-3 py-1 text-xs text-zinc dark:text-gray-200 mb-6 w-fit backdrop-blur-sm">
            <span className="size-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
            Practical AI Training
          </div>

          <h1
            id="hero-heading"
            className="text-black dark:text-white mb-4 leading-none tracking-tighter"
          >
            <img
              src="/snclogo.svg"
              alt="SNC AI Logo"
              className="h-40 md:h-56 lg:h-64 w-auto object-contain dark:brightness-0 dark:invert"
            />
          </h1>
          <p
            id="hero-subheading"
            className="text-zinc dark:text-gray-200 lg:text-5xl md:text-4xl text-2xl sm:text-3xl font-medium max-w-2xl leading-relaxed mb-8"
          >
            Practical AI, Training for Real-World Impact. Master the tools that define the future.
          </p>
        </div>

        {/* Right Side: Globe - Lower z-index to stay behind robot (z-30) */}
        <div className="flex-1 flex items-center justify-center min-h-[350px] md:min-h-[500px] w-full lg:-mr-32 overflow-visible relative z-30">
          <InteractiveGlobe
            size={globeSize}
            className="max-w-none scale-100 sm:scale-110 lg:scale-125"
            dotColor={globeColor}
            arcColor={globeArc}
            markerColor={globeMarker}
            markers={globalMarkers}
            connections={globalConnections}
          />
        </div>

      </div>
    </section>
  );
};

export default Hero;
